import { useMemo } from 'react';
import { useQualityControlTool } from '@/context/Qualitycontrol.context';
import { defectFieldMap } from '@/helpers/defect-form';
import { REQUIRED_FIELD } from '@/types/interfaces/defect-details';
import { FormSelectOption, SelectOptions } from '@/types/interfaces/form';
import { useGetDefects } from './use-get-defects';

interface SelectOptionProps {
  requiredFields?: REQUIRED_FIELD[];
}

const INITIAL_OPTIONS: SelectOptions = {
  colors: [] as FormSelectOption[],
  defectImportances: [] as FormSelectOption[],
  defectQuantities: [] as FormSelectOption[],
  defectSizes: [] as FormSelectOption[],
  materials: [] as FormSelectOption[],
  sizeTypes: [] as FormSelectOption[],
  categoryBrandModels: [] as FormSelectOption[],
  categories: [] as FormSelectOption[],
};

export const useSelectOptions = ({ requiredFields }: SelectOptionProps) => {
  const { qcData: { uuid = '' } = {} } = useQualityControlTool();

  const { data: defects, isLoading } = useGetDefects(uuid);

  const options = useMemo(() => {
    const result = INITIAL_OPTIONS;

    if (!defects || !requiredFields?.length) return result;

    requiredFields.forEach((f) => {
      const fields = defectFieldMap.get(f) || [];

      fields.forEach((keyName) => {
        switch (keyName) {
          case 'colors':
          case 'categoryBrandModels':
          case 'materials':
            result[keyName] = defects[keyName]
              .map((i) => ({
                label: i.name,
                value: i.id.toString(),
              }))
              .sort((a, b) => -b.label.localeCompare(a.label));
            break;
          case 'defectImportances':
          case 'defectQuantities':
          case 'defectSizes':
            result[keyName] = defects[keyName].map((i) => ({
              label: i.label,
              value: i.value,
            }));
            break;
          case 'sizeTypes':
            result[keyName] = defects[keyName].map((i) => ({
              label: i.type,
              value: i.type,
            }));
            break;
          case 'categories':
            result[keyName] = defects[keyName]
              .map((i) => ({
                label: i.completeName,
                value: i.id.toString(),
                legacyId: i.legacyId.toString(),
              }))
              .sort((a, b) => -b.label.localeCompare(a.label));
            break;
          default:
            break;
        }
      });
    });

    return result;
  }, [requiredFields, defects]);

  return { options, isLoading };
};
